"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * "Everything for your week, in one place." — owner feedback 2026-08-26 (e):
 * the static 6-card grid becomes an interactive guided tour. Left: the six
 * features as tab buttons with a live progress ring; right: an animated
 * preview panel (AnimatePresence swap) with a themed mini-visual per
 * feature. Auto-advances every 6s until the visitor interacts; fully
 * keyboard operable (real tabs semantics); reduced motion = no auto-advance
 * and instant swaps. All copy is the existing i18n content — nothing
 * invented.
 */

import { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { ChefHat, LineChart, Package, ShoppingCart, Sparkles, Sun } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"
import { DishArt } from "./DishArt"

const AUTO_ADVANCE_MS = 6000

function PreviewVisual({ id }: { id: string }) {
  // Small honest scene per feature — sample-styled, consistent with the hero.
  switch (id) {
    case "today":
      return (
        <div className="flex items-center gap-4">
          <DishArt dish="salad" size={84} />
          <DishArt dish="curry" size={68} className="-ml-8 mt-8" />
        </div>
      )
    case "plan":
      return (
        <div className="grid grid-cols-4 gap-1.5">
          {(["buddha", "salmon", "oats", "pasta", "curry", "salad", "buddha", "oats"] as const).map(
            (d, i) => (
              <DishArt key={i} dish={d} size={40} />
            )
          )}
        </div>
      )
    case "pantry":
      return (
        <div className="flex items-end gap-2">
          <DishArt dish="oats" size={72} />
          <div className="flex flex-col gap-1">
            {[72, 54, 38].map(w => (
              <div key={w} className="h-2 rounded-bb-full bg-green/25" style={{ width: w }} />
            ))}
          </div>
        </div>
      )
    case "shopping":
      return (
        <div className="flex flex-col gap-1.5">
          {[86, 64, 74, 48].map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <span
                className={`size-3 rounded-[4px] border ${i < 2 ? "border-green bg-green" : "border-line bg-paper"}`}
              />
              <div className="h-2 rounded-bb-full bg-ink/15" style={{ width: w }} />
            </div>
          ))}
        </div>
      )
    case "learning":
      return (
        <svg viewBox="0 0 120 48" width="120" height="48" aria-hidden="true">
          <polyline
            points="4,40 24,34 44,36 64,26 84,22 104,12"
            fill="none"
            stroke="#2d6a4f"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="104" cy="12" r="4" fill="#d8f184" stroke="#2d6a4f" strokeWidth="2" />
        </svg>
      )
    default:
      return <Sparkles aria-hidden="true" className="size-12 text-green" />
  }
}

export function ProductWalkthrough() {
  const { t } = useI18n()
  const reduce = useReducedMotion() ?? false
  const [active, setActive] = useState(0)
  const [interacted, setInteracted] = useState(false)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const cards = [
    {
      id: "today",
      icon: Sun,
      title: t("landingPage.walkthrough.todayTitle"),
      body: t("landingPage.walkthrough.todayBody"),
    },
    {
      id: "plan",
      icon: ChefHat,
      title: t("landingPage.walkthrough.planTitle"),
      body: t("landingPage.walkthrough.planBody"),
    },
    {
      id: "amy",
      icon: Sparkles,
      title: t("landingPage.walkthrough.amyTitle"),
      body: t("landingPage.walkthrough.amyBody"),
      chips: [
        t("landingPage.walkthrough.amyPrompt1"),
        t("landingPage.walkthrough.amyPrompt2"),
        t("landingPage.walkthrough.amyPrompt3"),
      ],
    },
    {
      id: "pantry",
      icon: Package,
      title: t("landingPage.walkthrough.pantryTitle"),
      body: t("landingPage.walkthrough.pantryBody"),
    },
    {
      id: "shopping",
      icon: ShoppingCart,
      title: t("landingPage.walkthrough.shoppingTitle"),
      body: t("landingPage.walkthrough.shoppingBody"),
    },
    {
      id: "learning",
      icon: LineChart,
      title: t("landingPage.walkthrough.learningTitle"),
      body: t("landingPage.walkthrough.learningBody"),
    },
  ]

  useEffect(() => {
    if (reduce || interacted) return
    timer.current = setInterval(() => setActive(a => (a + 1) % cards.length), AUTO_ADVANCE_MS)
    return () => {
      if (timer.current) clearInterval(timer.current)
    }
  }, [reduce, interacted, cards.length])

  function select(i: number) {
    setInteracted(true)
    setActive(i)
  }

  const card = cards[active]!

  return (
    <section id="amy" className="scroll-mt-[12vh] border-b border-line bg-cream">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.walkthrough.eyebrow")}
          </p>
          <h2 className="font-display mt-2 text-3xl font-semibold text-ink md:text-4xl">
            {t("landingPage.walkthrough.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.walkthrough.lead")}</p>
        </Reveal>

        <div className="mt-10 grid gap-6 md:grid-cols-[minmax(0,340px)_1fr] md:items-stretch">
          <div
            role="tablist"
            aria-orientation="vertical"
            className="flex flex-row gap-2 overflow-x-auto pb-1 md:flex-col md:overflow-visible md:pb-0"
          >
            {cards.map((c, i) => (
              <button
                key={c.id}
                role="tab"
                id={`walkthrough-tab-${c.id}`}
                aria-selected={i === active}
                aria-controls="walkthrough-panel"
                onClick={() => select(i)}
                className={`flex min-h-11 shrink-0 items-center gap-3 rounded-bb-lg border px-4 py-2.5 text-left transition-all ${
                  i === active
                    ? "border-green/40 bg-paper shadow-md shadow-green/10"
                    : "border-line bg-paper/60 hover:border-green/25 hover:bg-paper"
                }`}
              >
                <c.icon
                  aria-hidden="true"
                  className={`size-5 shrink-0 ${i === active ? "text-green" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-sm font-semibold ${i === active ? "text-ink" : "text-ink-2"}`}
                >
                  {c.title}
                </span>
                {i === active && !reduce && !interacted && (
                  <motion.span
                    aria-hidden="true"
                    className="ml-auto h-1 shrink-0 rounded-bb-full bg-lime"
                    initial={{ width: 0 }}
                    animate={{ width: 24 }}
                    transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: "linear" }}
                  />
                )}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id="walkthrough-panel"
            aria-labelledby={`walkthrough-tab-${card.id}`}
            className="relative min-h-[260px] overflow-hidden rounded-bb-xl border border-line bg-paper p-6 md:p-8"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-16 -right-16 size-48 rounded-full bg-lime/25 blur-3xl"
            />
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={card.id}
                initial={reduce ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex h-full flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  <span className="flex size-10 items-center justify-center rounded-bb-lg bg-green/10">
                    <card.icon aria-hidden="true" className="size-5 text-green" />
                  </span>
                  <h3 className="font-display text-2xl font-semibold text-ink">{card.title}</h3>
                </div>
                <p className="max-w-prose text-ink-2">{card.body}</p>
                {card.chips ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {card.chips.map(chip => (
                      <span
                        key={chip}
                        className="rounded-bb-full border border-green/25 bg-lime/20 px-3 py-1.5 text-sm font-medium text-ink"
                      >
                        “{chip}”
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-auto flex justify-end pt-2">
                    <PreviewVisual id={card.id} />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}
